import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTeam } from '../contexts/TeamContext';

type GuideTab = 'welcome' | 'quickstart' | 'channels' | 'security' | 'team' | 'workspaces';

const TABS: { id: GuideTab; label: string }[] = [
	{ id: 'welcome', label: 'Welcome' },
	{ id: 'quickstart', label: 'Quick Start' },
	{ id: 'channels', label: 'Channels' },
	{ id: 'security', label: 'Security' },
	{ id: 'team', label: 'Team' },
	{ id: 'workspaces', label: 'Workspaces' },
];

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6">
			<h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">{title}</h3>
			{children}
		</div>
	);
}

function InlineCode({ children }: { children: React.ReactNode }) {
	return (
		<code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono">
			{children}
		</code>
	);
}

function CheckListItem({ children }: { children: React.ReactNode }) {
	return (
		<li className="flex items-start gap-3">
			<svg className="w-4 h-4 text-primary-500 dark:text-primary-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
			</svg>
			<span className="text-sm text-slate-600 dark:text-slate-400">{children}</span>
		</li>
	);
}

function ActionLink({ to, children }: { to: string; children: React.ReactNode }) {
	return (
		<Link
			to={to}
			className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
		>
			{children}
		</Link>
	);
}

function WelcomeTab() {
	return (
		<div className="space-y-8">
			{/* Hero */}
			<div className="bg-gradient-to-r from-primary-500 to-secondary-600 rounded-xl px-8 py-10 text-white">
				{/* Logos */}
				<div className="flex items-center gap-4 mb-6">
					<img src="/img/logo.png" alt="ClawdGate" className="w-14 h-14 rounded-xl" />
					<span className="text-2xl font-light text-white/60">+</span>
					<img src="/img/openclaw-logo.png" alt="OpenClaw" className="w-14 h-14 rounded-xl" />
				</div>
				<h2 className="text-2xl font-bold mb-2">Your AI Workspaces, Ready in Minutes</h2>
				<p className="text-primary-100 text-sm leading-relaxed max-w-2xl">
					Create isolated AI environments, assign your team, and chat from any platform.
					Built-in security protects every interaction.
				</p>
			</div>

			{/* Use Cases */}
			<div>
				<h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">What You Can Do with ClawdGate</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{/* Personal AI */}
					<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5">
						<div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400 mb-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
							</svg>
						</div>
						<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Personal AI</h4>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Secure your own Claude/GPT workspace with threat detection and full audit trail.
						</p>
					</div>

					{/* Team Collaboration */}
					<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5">
						<div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg flex items-center justify-center text-secondary-600 dark:text-secondary-400 mb-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
							</svg>
						</div>
						<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Team Collaboration</h4>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Share AI workspaces across your team with role-based access control.
						</p>
					</div>

					{/* Multi-Client Management */}
					<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5">
						<div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 mb-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
							</svg>
						</div>
						<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Multi-Workspace Management</h4>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Run separate AI workspaces with their own skills, channels, AI agent configuration, security policies, and providers.
						</p>
					</div>

					{/* Enterprise Governance */}
					<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5">
						<div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-600 dark:text-purple-400 mb-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
							</svg>
						</div>
						<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Enterprise Governance</h4>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Department-level AI access, compliance audit trails, and centralized policy management.
						</p>
					</div>
				</div>
			</div>

			{/* Protection Overview */}
			<div>
				<h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">
					How ClawdGate Protects Your Workspaces
				</h3>

				{/* Flow diagram */}
				<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6 mb-4">
					<div className="flex flex-col md:flex-row items-center justify-between gap-2">
						{[
							'Your Message',
							'Pattern Detection',
							'AI Threat Analysis',
							'Policy Engine',
							'AI Workspace',
						].map((stage, i, arr) => (
							<div key={stage} className="flex items-center gap-2 flex-1 min-w-0">
								<div className={`flex-1 text-center px-3 py-3 rounded-lg text-xs font-semibold ${
									i === 0
										? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
										: i === arr.length - 1
										? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white'
										: 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
								}`}>
									{stage}
								</div>
								{i < arr.length - 1 && (
									<svg className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 rotate-90 md:rotate-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
									</svg>
								)}
							</div>
						))}
					</div>
				</div>

				{/* Explanations */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="flex items-start gap-3">
						<div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
							<span className="text-xs font-bold text-primary-600 dark:text-primary-400">1</span>
						</div>
						<div>
							<p className="text-sm font-medium text-slate-900 dark:text-white">Pattern Detection</p>
							<p className="text-sm text-slate-600 dark:text-slate-400">Fast pattern matching catches known threats like prompt injection and credential exfiltration attempts.</p>
						</div>
					</div>
					<div className="flex items-start gap-3">
						<div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
							<span className="text-xs font-bold text-primary-600 dark:text-primary-400">2</span>
						</div>
						<div>
							<p className="text-sm font-medium text-slate-900 dark:text-white">AI Threat Analysis</p>
							<p className="text-sm text-slate-600 dark:text-slate-400">AI-powered threat detection analyzes context to catch sophisticated attacks like indirect injection and social engineering.</p>
						</div>
					</div>
					<div className="flex items-start gap-3">
						<div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
							<span className="text-xs font-bold text-primary-600 dark:text-primary-400">3</span>
						</div>
						<div>
							<p className="text-sm font-medium text-slate-900 dark:text-white">Policy Engine</p>
							<p className="text-sm text-slate-600 dark:text-slate-400">Configurable response per severity — allow, review, block, or require MFA.</p>
						</div>
					</div>
					<div className="flex items-start gap-3">
						<div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
							<span className="text-xs font-bold text-primary-600 dark:text-primary-400">4</span>
						</div>
						<div>
							<p className="text-sm font-medium text-slate-900 dark:text-white">Audit Trail</p>
							<p className="text-sm text-slate-600 dark:text-slate-400">Every interaction logged and reviewable from the Events page.</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function QuickStartTab() {
	const steps: {
		title: string;
		description: string;
		link: string;
		buttonLabel: string;
	}[] = [
		{
			title: 'Configure an AI Provider',
			description: 'Go to Settings and add your Anthropic, OpenAI, or Google AI API key under Workspace Defaults.',
			link: '/settings',
			buttonLabel: 'Go to Settings',
		},
		{
			title: 'Install OpenClaw and Create Your First Workspace',
			description: 'Go to Devices and follow the install guide. Run the one-line installer, register with your token, and start the service. Your first workspace appears automatically.',
			link: '/machines',
			buttonLabel: 'Go to Devices',
		},
		{
			title: 'Add a Communication Channel (Optional)',
			description: 'Add your Telegram bot token, Slack bot, or Discord bot under Channel Keys in Settings. Or just use the built-in Chat.',
			link: '/settings',
			buttonLabel: 'Go to Settings',
		},
		{
			title: 'Start Chatting',
			description: 'Open Chat in the sidebar, or send a message from your linked Telegram, Slack, or Discord.',
			link: '/chat',
			buttonLabel: 'Open Chat',
		},
	];

	return (
		<div className="space-y-4">
			{steps.map((step, i) => (
				<div
					key={i}
					className="border border-slate-200 dark:border-slate-800 rounded-xl p-5 flex items-start gap-4"
				>
					<div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
						{i + 1}
					</div>
					<div className="flex-1 min-w-0">
						<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">{step.title}</h4>
						<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{step.description}</p>
						<ActionLink to={step.link}>{step.buttonLabel}</ActionLink>
					</div>
				</div>
			))}
		</div>
	);
}

function ChannelsTab() {
	return (
		<div className="space-y-3">
			{/* Telegram */}
			<details open className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
				<summary className="px-5 py-4 cursor-pointer text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors list-none flex items-center justify-between">
					<span>Setting Up Telegram</span>
					<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</summary>
				<div className="px-5 pb-5 pt-1 border-t border-slate-200 dark:border-slate-800">
					<ol className="space-y-2.5 list-none">
						{[
							'Create a bot via @BotFather on Telegram, copy the bot token.',
							<>Go to Settings &gt; Channel Keys &gt; Add Telegram, paste the token.</>,
							'Assign the channel key to a workspace.',
							<>Team members link their Telegram in Profile &gt; Linked Accounts using <InlineCode>!link CODE</InlineCode>.</>,
						].map((step, i) => (
							<li key={i} className="flex items-start gap-3">
								<span className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0 mt-0.5">
									{i + 1}
								</span>
								<span className="text-sm text-slate-600 dark:text-slate-400">{step}</span>
							</li>
						))}
					</ol>
				</div>
			</details>

			{/* Slack */}
			<details className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
				<summary className="px-5 py-4 cursor-pointer text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors list-none flex items-center justify-between">
					<span>Setting Up Slack</span>
					<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</summary>
				<div className="px-5 pb-5 pt-1 border-t border-slate-200 dark:border-slate-800">
					<ol className="space-y-2.5 list-none">
						{[
							'Create a Slack App, enable Socket Mode, get bot token + app token + signing secret.',
							<>Go to Settings &gt; Channel Keys &gt; Add Slack, enter all three values.</>,
							'Assign the channel key to a workspace.',
							<>Team members link via <InlineCode>!link CODE</InlineCode> in a DM with the bot.</>,
						].map((step, i) => (
							<li key={i} className="flex items-start gap-3">
								<span className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0 mt-0.5">
									{i + 1}
								</span>
								<span className="text-sm text-slate-600 dark:text-slate-400">{step}</span>
							</li>
						))}
					</ol>
				</div>
			</details>

			{/* Discord */}
			<details className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
				<summary className="px-5 py-4 cursor-pointer text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors list-none flex items-center justify-between">
					<span>Setting Up Discord</span>
					<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</summary>
				<div className="px-5 pb-5 pt-1 border-t border-slate-200 dark:border-slate-800">
					<ol className="space-y-2.5 list-none">
						{[
							'Create a Discord Application, get bot token + application ID.',
							<>Go to Settings &gt; Channel Keys &gt; Add Discord, enter both values.</>,
							'Assign the channel key to a workspace.',
							<>Team members link via <InlineCode>!link CODE</InlineCode> as a DM to the bot.</>,
						].map((step, i) => (
							<li key={i} className="flex items-start gap-3">
								<span className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0 mt-0.5">
									{i + 1}
								</span>
								<span className="text-sm text-slate-600 dark:text-slate-400">{step}</span>
							</li>
						))}
					</ol>
				</div>
			</details>

			{/* Linking */}
			<details className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
				<summary className="px-5 py-4 cursor-pointer text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors list-none flex items-center justify-between">
					<span>Linking Your Account (for All Members)</span>
					<svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</summary>
				<div className="px-5 pb-5 pt-1 border-t border-slate-200 dark:border-slate-800">
					<ul className="space-y-2.5 list-none">
						<CheckListItem>Go to Profile &gt; Linked Accounts.</CheckListItem>
						<CheckListItem>Click Link [Platform], copy the verification code.</CheckListItem>
						<CheckListItem>Send <InlineCode>!link CODE</InlineCode> to the bot on that platform.</CheckListItem>
						<CheckListItem>Once verified, messages route through ClawdGate to your workspace.</CheckListItem>
					</ul>
				</div>
			</details>
		</div>
	);
}

function SecurityTab() {
	return (
		<div className="space-y-4">
			<SectionCard title="Threat Detection Policies">
				<ul className="space-y-2">
					<CheckListItem>Navigate to Settings &gt; Workspace Defaults &gt; Security tab.</CheckListItem>
					<CheckListItem>Enable or disable threat protection.</CheckListItem>
					<CheckListItem>Configure response per severity (Low / Medium / High / Critical): Allow, Review, Block, or MFA Challenge.</CheckListItem>
					<CheckListItem>Per-workspace overrides are available from each workspace config page.</CheckListItem>
				</ul>
			</SectionCard>

			<SectionCard title="Privileged Access Management (PAM)">
				<ul className="space-y-2">
					<CheckListItem>Protects: process control (start/stop agents), threat responses, policy changes, and credential management.</CheckListItem>
					<CheckListItem>Enable at Profile &gt; Security &gt; Enable Privileged Access.</CheckListItem>
					<CheckListItem>Setup: scan the QR code with an authenticator app (Google Authenticator, Authy, 1Password).</CheckListItem>
					<CheckListItem>Usage: enter your TOTP code when prompted for privileged actions — human-in-the-loop approval.</CheckListItem>
				</ul>
			</SectionCard>

			<SectionCard title="Reviewing Activity & Audit Trail">
				<ul className="space-y-2 mb-4">
					<CheckListItem>Go to the Events page to view all AI interactions.</CheckListItem>
					<CheckListItem>Filter by event type, user, or workspace.</CheckListItem>
					<CheckListItem>Look for: blocked threats, policy violations, and privileged action approvals.</CheckListItem>
					<CheckListItem>Full audit trail for compliance.</CheckListItem>
				</ul>
				<ActionLink to="/events">View Events</ActionLink>
			</SectionCard>
		</div>
	);
}

function TeamTab() {
	return (
		<div className="space-y-4">
			<SectionCard title="For Admins / Owners">
				<ul className="space-y-2 mb-4">
					<CheckListItem>Invite members: Team page &gt; Invite Member (email + role).</CheckListItem>
					<CheckListItem>Roles explained: <strong className="text-slate-800 dark:text-slate-200">Owner</strong> (full control), <strong className="text-slate-800 dark:text-slate-200">Admin</strong> (manage team + workspaces), <strong className="text-slate-800 dark:text-slate-200">Member</strong> (chat + profile only).</CheckListItem>
					<CheckListItem>Manage workspace access: Team page &gt; Workspaces button on the member row.</CheckListItem>
					<CheckListItem>Change roles: use the role dropdown on non-owner members.</CheckListItem>
				</ul>
				<ActionLink to="/team">Go to Team</ActionLink>
			</SectionCard>

			<SectionCard title="For Members">
				<ul className="space-y-2 mb-4">
					<CheckListItem>What you can access: Chat and Profile pages. Only workspaces you have been granted access to.</CheckListItem>
					<CheckListItem>Link messaging platforms: Profile &gt; Linked Accounts, generate a code, send <InlineCode>!link CODE</InlineCode> to the bot.</CheckListItem>
					<CheckListItem>Enable PAM if required: Profile &gt; Security.</CheckListItem>
				</ul>
				<div className="flex gap-2">
					<ActionLink to="/profile">Go to Profile</ActionLink>
					<ActionLink to="/chat">Open Chat</ActionLink>
				</div>
			</SectionCard>
		</div>
	);
}

function WorkspacesTab() {
	return (
		<div className="space-y-4">
			<SectionCard title="Creating Workspaces">
				<ul className="space-y-2 mb-4">
					<CheckListItem>Install the connector from the Devices page.</CheckListItem>
					<CheckListItem>The connector registers with ClawdGate and creates and manages workspaces through the connector.</CheckListItem>
					<CheckListItem>Multiple connectors = multiple devices and workspaces, each independent.</CheckListItem>
				</ul>
				<ActionLink to="/machines">Go to Devices</ActionLink>
			</SectionCard>

			<SectionCard title="Configuring All Workspaces">
				<ul className="space-y-2">
					<CheckListItem>Navigate: Setting page &rarr; click workspace &rarr; config.</CheckListItem>
					<CheckListItem>AI Providers: choose models, set fallback order, add API keys.</CheckListItem>
					<CheckListItem>Security policies: policies apply to all workspaces.</CheckListItem>
					<CheckListItem>Integrations: connect channels via channel keys.</CheckListItem>					
					<CheckListItem>Navigate: Team &gt; Workspaces &gt;control which team members can use this workspace.</CheckListItem>
					<CheckListItem>Navigate: Devices &gt; Select Device, click Configure in the Workspaces: override team defaults.</CheckListItem>
				</ul>
			</SectionCard>

			<SectionCard title="Managing Multiple Workspaces">
				<ul className="space-y-2 mb-4">
					<CheckListItem>Isolation: separate policies, providers, channels, skills and member access per workspace.</CheckListItem>
					<CheckListItem>Overview available from the Dashboard and Devices pages.</CheckListItem>
					<CheckListItem>Use cases: per project, per client, per department, or per AI model.</CheckListItem>
				</ul>
				<div className="flex gap-2">
					<ActionLink to="/dashboard">Go to Dashboard</ActionLink>
					<ActionLink to="/machines">Go to Devices</ActionLink>
				</div>
			</SectionCard>
		</div>
	);
}

export default function GettingStartedPage() {
	const navigate = useNavigate();
	const { currentTeam } = useTeam();
	const isMember = currentTeam?.role === 'member';
	const [activeTab, setActiveTab] = useState<GuideTab>('welcome');

	const handleDismiss = () => {
		localStorage.setItem('clawdgate-onboarded', 'true');
		navigate(isMember ? '/chat' : '/dashboard', { replace: true });
	};

	return (
		<Layout title="Getting Started" subtitle="Your complete guide to ClawdGate">
			<div className="max-w-4xl mx-auto">
				<div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
					{/* Tab bar */}
					<div className="border-b border-slate-200 dark:border-slate-800 px-6 pt-4 overflow-x-auto">
						<div className="flex gap-1 min-w-max">
							{TABS.map((tab) => {
								const isActive = activeTab === tab.id;
								return (
									<button
										key={tab.id}
										onClick={() => setActiveTab(tab.id)}
										className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-all duration-150 cursor-pointer whitespace-nowrap ${
											isActive
												? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white shadow-sm'
												: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
										}`}
									>
										{tab.label}
									</button>
								);
							})}
						</div>
					</div>

					{/* Tab content */}
					<div className="p-6 sm:p-8">
						{activeTab === 'welcome' && <WelcomeTab />}
						{activeTab === 'quickstart' && <QuickStartTab />}
						{activeTab === 'channels' && <ChannelsTab />}
						{activeTab === 'security' && <SecurityTab />}
						{activeTab === 'team' && <TeamTab />}
						{activeTab === 'workspaces' && <WorkspacesTab />}
					</div>

					{/* Footer */}
					<div className="px-6 sm:px-8 py-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800">
						<div className="flex items-center justify-between">
							<p className="text-sm text-slate-500 dark:text-slate-400">
								You can revisit this page anytime from the sidebar.
							</p>
							<button
								onClick={handleDismiss}
								className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm cursor-pointer"
							>
								Get Started
							</button>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
